/**
 * WMS purchase order facade: delegates to picqer-purchase-orders or channeldock-purchse-orders
 * (Edge function slug is intentionally misspelled to match deployed Supabase function name.)
 * based on wms_provider. Same request/response shape for booking PO flow.
 */
import { FunctionsHttpError } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import type { WmsProvider } from './wmsOrdersService';

/** Supabase invoke sets error (not parsed JSON) on non-2xx; body is on FunctionsHttpError.context (Response). */
async function readEdgeFunctionErrorBody(error: unknown): Promise<string | null> {
  if (!(error instanceof FunctionsHttpError)) return null;
  const ctx = error.context;
  if (!ctx || typeof (ctx as Response).text !== 'function') return null;
  try {
    const text = await (ctx as Response).text();
    const t = text?.trim();
    return t || null;
  } catch {
    return null;
  }
}

export type { WmsProvider };

export interface WmsPurchaseOrderResponse {
  success: boolean;
  data?: {
    purchaseOrderId?: number;
    deliveryId?: number;
    picqerReference?: string;
    channeldockReference?: string;
    bookingReference: string;
    status: string;
  };
  error?: string;
  productAnalysis?: {
    total: number;
    ready: number;
    needingSetup: number;
    products: unknown[];
  };
  canCreatePurchaseOrder?: boolean;
  nextSteps?: string[];
  existingPurchaseOrder?: {
    id: number;
    picqerId?: number;
    channeldockDeliveryId?: number;
    reference?: string;
  };
}

export const WmsPurchaseOrderService = {
  async analyzeProductsForBooking(
    bookingId: number,
    _wmsProvider: WmsProvider
  ): Promise<{ success: boolean; data?: WmsPurchaseOrderResponse['productAnalysis']; error?: string }> {
    const fn = _wmsProvider === 'channeldock' ? 'channeldock-purchse-orders' : 'picqer-purchase-orders';
    try {
      const { data, error } = await supabase.functions.invoke(fn, {
        body: { bookingId, analyzeOnly: true },
      });
      if (error) {
        const bodyText = await readEdgeFunctionErrorBody(error);
        return {
          success: false,
          error: bodyText ?? (error instanceof Error ? error.message : 'Analysis failed'),
        };
      }
      const result = data as WmsPurchaseOrderResponse & { productAnalysis?: WmsPurchaseOrderResponse['productAnalysis'] };
      if (result?.success && result.productAnalysis) {
        return { success: true, data: result.productAnalysis };
      }
      if (result?.success === false) {
        return { success: false, error: result.error ?? 'Analysis failed', data: result.productAnalysis };
      }
      return { success: false, error: (result?.error as string) ?? 'Failed to analyze products' };
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to analyze products';
      return { success: false, error: msg };
    }
  },

  async createPurchaseOrderForBooking(
    bookingId: number,
    wmsProvider: WmsProvider
  ): Promise<WmsPurchaseOrderResponse> {
    const fn = wmsProvider === 'channeldock' ? 'channeldock-purchse-orders' : 'picqer-purchase-orders';
    try {
      const { data, error } = await supabase.functions.invoke(fn, {
        body: { bookingId },
      });
      if (error) {
        const bodyText = await readEdgeFunctionErrorBody(error);
        return {
          success: false,
          error: bodyText ?? (error instanceof Error ? error.message : 'Failed to create purchase order'),
        };
      }
      const result = data as WmsPurchaseOrderResponse;
      if (result != null) return result;
      return { success: false, error: 'No response from purchase order service' };
    } catch (err) {
      const bodyText = await readEdgeFunctionErrorBody(err);
      const msg = err instanceof Error ? err.message : 'Failed to create purchase order';
      return { success: false, error: bodyText ?? msg };
    }
  },
};
